import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Collapse, IconButton, Menu, MenuItem,
  Grid, Pagination, Paper, Snackbar, TextField, Tooltip, Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import {
  Clear as ClearIcon, Description as DescriptionIcon, Download as DownloadIcon,
  FilterAltOffOutlined as FilterAltOffIcon, FilterAltOutlined as FilterAltIcon,
  InfoOutlined as InfoIcon, PictureAsPdf as PdfIcon,
  Print as PrintIcon, RestartAlt as RestartAltIcon, Search as SearchIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import PartnerMainContainer from '../../../components/PartnerMainContainer';
import ProcurementService from '../../../services/ProcurementService';
import { exporterTableauPartenaires } from '../Export/exporterTableauPartenaires';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import TitlePartner from '../component/Title';
import MarcheDetailsModal from './MarcheDetailsModal';
import {
  formatDate, formatPartnerSource, normalizeFinancement,
} from '../../../helpers/PartnerHelper';
import usePartnerFilterConsultation from '../hooks/usePartnerFilterConsultation';
import {
  partnerFilterButtonSx, partnerFilterFieldSx, partnerGroupButtonSx,
  partnerDataGridSx, partnerTableColors,
} from '../Parterns/partnerListTheme';

const GROUP_OPTIONS = [
  ['none', 'Aucun'], ['direction', 'Direction'], ['financement', 'Financement'],
  ['composante', 'Composante'], ['situation', 'Situation'],
];

const normalize = (value) => String(value || '').normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').toUpperCase().trim();

const situationColor = (value) => {
  const key = normalize(value);
  if (key.includes('RESIL')) return { color: '#b42318', bgcolor: '#fee4e2' };
  if (key.includes('COURS')) return { color: '#067647', bgcolor: '#dcfae6' };
  return { color: '#475467', bgcolor: '#eef2f6' };
};


const Procurement = () => {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState('none');
  const [directions, setDirections] = useState([]);
  const [fundings, setFundings] = useState([]);
  const [components, setComponents] = useState([]);
  const [situations, setSituations] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [message, setMessage] = useState('');

  const auditFilters = useMemo(() => ({
    search, group_by: groupBy, directions, financements: fundings,
    composantes: components, situations, date_from: dateFrom, date_to: dateTo,
  }), [search, groupBy, directions, fundings, components, situations, dateFrom, dateTo]);
  usePartnerFilterConsultation(auditFilters, (filters) => ProcurementService.getAllProcurements(filters));

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      const response = await ProcurementService.getAllProcurements();
      if (active) {
        setMarkets((Array.isArray(response?.data) ? response.data : []).map((row) => ({
          ...row,
          financement: normalizeFinancement(row.financement) || row.financement,
        })));
        setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  useEffect(() => setPage(0), [search, groupBy, directions, fundings, components, situations, dateFrom, dateTo]);

  const options = useMemo(() => ({
    directions: [...new Set(markets.map((row) => row.direction).filter(Boolean))].sort(),
    fundings: [...new Set(markets.map((row) => row.financement).filter(Boolean))].sort(),
    components: [...new Set(markets.map((row) => row.composante).filter(Boolean))].sort(),
    situations: [...new Set(markets.map((row) => row.situation).filter(Boolean))].sort(),
  }), [markets]);

  const filtered = useMemo(() => markets.filter((row) => {
    const haystack = normalize(`${row.numero} ${row.partenaire} ${row.objet}`);
    if (search && !haystack.includes(normalize(search))) return false;
    if (directions.length && !directions.includes(row.direction)) return false;
    if (fundings.length && !fundings.includes(row.financement)) return false;
    if (components.length && !components.includes(row.composante)) return false;
    if (situations.length && !situations.includes(row.situation)) return false;
    const start = row.debut || '';
    const end = row.fin || '';
    if (dateFrom && end && end < dateFrom) return false;
    if (dateTo && start && start > dateTo) return false;
    return true;
  }).sort((a, b) => String(a.numero || '').localeCompare(String(b.numero || ''), 'fr')), [
    markets, search, directions, fundings, components, situations, dateFrom, dateTo,
  ]);

  const groupedMarkets = useMemo(() => {
    if (groupBy === 'none') return [];
    const emptyLabels = {
      direction: 'Aucune direction', financement: 'Aucun financement',
      composante: 'Aucune composante', situation: 'Aucune situation',
    };
    const groups = filtered.reduce((accumulator, row) => {
      const label = row[groupBy] || emptyLabels[groupBy];
      accumulator[label] = [...(accumulator[label] || []), row];
      return accumulator;
    }, {});
    return Object.entries(groups).sort(([left], [right]) => left.localeCompare(right, 'fr'));
  }, [filtered, groupBy]);

  const groupedPages = useMemo(() => {
    if (groupBy === 'none') return [];
    const pages = [];
    let currentPage = [];
    let currentMarketCount = 0;

    groupedMarkets.forEach(([label, groupRows], groupIndex) => {
      if (currentMarketCount > 0 && currentMarketCount + groupRows.length > rowsPerPage) {
        pages.push(currentPage);
        currentPage = [];
        currentMarketCount = 0;
      }
      currentPage.push({
        id: `group-${groupBy}-${groupIndex}`,
        isGroup: true,
        label,
        count: groupRows.length,
      });
      currentPage.push(...groupRows);
      currentMarketCount += groupRows.length;
    });

    if (currentPage.length) pages.push(currentPage);
    return pages;
  }, [groupBy, groupedMarkets, rowsPerPage]);

  const displayedRows = groupBy === 'none' ? filtered : (groupedPages[page] || groupedPages[0] || []);

  const resetFilters = () => {
    setSearch(''); setDirections([]); setFundings([]); setComponents([]);
    setSituations([]); setDateFrom(''); setDateTo(''); setGroupBy('none');
  };

  const openDetails = (row) => {
    if (!row || row.isGroup) return;
    setSelectedMarket(row);
    setDetailsOpen(true);
  };

  const columns = [
    {
      field: 'numero', headerName: 'N° marché', minWidth: 145, flex: 0.8,
      renderCell: ({ row, value }) => row.isGroup
        ? <Typography sx={{ fontSize: 12, fontWeight: 800 }}>{row.label} ({row.count})</Typography>
        : <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{value || '—'}</Typography>,
    },
    { field: 'partenaire', headerName: 'Titulaire', minWidth: 190, flex: 1.2, renderCell: ({ row, value }) => row.isGroup ? null : (value || '—') },
    { field: 'direction', headerName: 'Direction', minWidth: 105, flex: 0.65, renderCell: ({ row, value }) => row.isGroup ? null : (value || '—') },
    { field: 'financement', headerName: 'Financement', minWidth: 125, flex: 0.75, renderCell: ({ row, value }) => row.isGroup ? null : (value || '—') },
    { field: 'composante', headerName: 'Composante', minWidth: 115, flex: 0.7, renderCell: ({ row, value }) => row.isGroup ? null : (value || '—') },
    { field: 'debut', headerName: 'Début', minWidth: 105, flex: 0.65, renderCell: ({ row, value }) => row.isGroup ? null : formatDate(value) },
    { field: 'fin', headerName: 'Fin', minWidth: 105, flex: 0.65, renderCell: ({ row, value }) => row.isGroup ? null : formatDate(value) },
    {
      field: 'situation', headerName: 'Situation', minWidth: 125, flex: 0.75,
      renderCell: ({ row, value }) => row.isGroup ? null : <Chip size="small" label={value || '—'} sx={{ ...situationColor(value), fontWeight: 700 }} />,
    },
    {
      field: 'source_db', headerName: 'Base source', minWidth: 155, flex: 0.9,
      renderCell: ({ row, value }) => row.isGroup ? null : formatPartnerSource(value),
    },
    {
      field: 'details', headerName: 'Détails', width: 85, sortable: false, filterable: false, align: 'center', headerAlign: 'center',
      renderCell: ({ row }) => row.isGroup ? null : (
        <Tooltip title="Afficher le détail du marché">
          <IconButton size="small" onClick={(event) => { event.stopPropagation(); openDetails(row); }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const exportRows = filtered.map((row) => ({
    'N° marché': row.numero || '', Titulaire: row.partenaire || '',
    Direction: row.direction || '', Financement: row.financement || '',
    Composante: row.composante || '', Début: formatDate(row.debut), Fin: formatDate(row.fin),
    Situation: row.situation || '', 'Base source': formatPartnerSource(row.source_db),
  }));

  const exporter = (format) => {
    exporterTableauPartenaires({
      format, lignes: exportRows,
      entetes: ['N° marché', 'Titulaire', 'Direction', 'Financement', 'Composante',
        'Début', 'Fin', 'Situation', 'Base source'],
      titre: 'Liste des marchés', nomFichier: 'marches', nomFeuille: 'Marchés',
      audit: { menu: 'Marches', format: format === 'xlsx' ? 'excel' : format,
        filters: auditFilters, rowCount: filtered.length },
    });
    setExportAnchor(null);
    if (format !== 'print') setMessage(`Export ${format === 'xlsx' ? 'Excel' : format.toUpperCase()} terminé.`);
  };
  const exportExcel = () => exporter('xlsx');
  const exportCsv = () => exporter('csv');
  const exportPdf = () => exporter('pdf');
  const printRows = () => exporter('print');

  const multiFilter = (label, value, setter, choices) => (
    <Autocomplete multiple size="small" options={choices} value={value} onChange={(_, next) => setter(next)}
      renderInput={(params) => <TextField {...params} label={label} sx={partnerFilterFieldSx} />}
      sx={{ minWidth: 190, flex: 1 }} />
  );

  return (
    <PartnerMainContainer>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={loading} />
      <TitlePartner title="Marchés" sub={`(${filtered.length} / ${markets.length} contrats)`} icon="📋" />

      <Paper elevation={0} sx={{ border: '1px solid #cbd8e6', borderRadius: '8px', mb: 2, p: 1.25 }}>
        <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: 'space-between', mb: filtersVisible ? 1.5 : 0 }}>
          <Typography color="#52677c" fontSize={10} fontWeight={700} letterSpacing="0.05em">FILTRES</Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end', ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}>
            {filtersVisible && <Button color="inherit" disabled={!search && !directions.length && !fundings.length && !components.length && !situations.length && !dateFrom && !dateTo && groupBy === 'none'} onClick={resetFilters} size="small" startIcon={<RestartAltIcon />} sx={{ ...partnerFilterButtonSx, borderColor: '#9fb1c3', color: partnerTableColors.dark, '&:hover': { backgroundColor: '#f3f7fa', borderColor: partnerTableColors.dark } }} variant="outlined">Réinitialiser</Button>}
            <Button color="inherit" onClick={() => setFiltersVisible((value) => !value)} size="small" startIcon={filtersVisible ? <FilterAltOffIcon /> : <FilterAltIcon />} sx={{ ...partnerFilterButtonSx, borderColor: '#9fb1c3', color: partnerTableColors.dark, '&:hover': { backgroundColor: '#f3f7fa', borderColor: partnerTableColors.dark } }} variant="outlined">
              {filtersVisible ? 'Masquer filtres' : 'Afficher filtres'}
            </Button>
            <Button disabled={!filtered.length} onClick={(event) => setExportAnchor(event.currentTarget)} size="small" startIcon={<DownloadIcon />} sx={{ ...partnerFilterButtonSx, backgroundColor: partnerTableColors.gold, color: partnerTableColors.text, fontWeight: 800, '&:hover': { backgroundColor: '#c39b2e' } }} variant="contained">Exporter</Button>
          </Box>
          <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
            <MenuItem onClick={exportExcel}><TableChartIcon sx={{ mr: 1 }} />Excel</MenuItem>
            <MenuItem onClick={exportCsv}><DescriptionIcon sx={{ mr: 1 }} />CSV</MenuItem>
            <MenuItem onClick={exportPdf}><PdfIcon sx={{ mr: 1 }} />PDF</MenuItem>
            <MenuItem onClick={printRows}><PrintIcon sx={{ mr: 1 }} />Imprimer</MenuItem>
          </Menu>
        </Box>
        <Collapse in={filtersVisible} timeout="auto" unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={6} md={3}>{multiFilter('Direction', directions, setDirections, options.directions)}</Grid>
              <Grid item xs={12} sm={6} md={3}>{multiFilter('Financement', fundings, setFundings, options.fundings)}</Grid>
              <Grid item xs={12} sm={6} md={3}>{multiFilter('Composante', components, setComponents, options.components)}</Grid>
              <Grid item xs={12} sm={6} md={3}>{multiFilter('Situation', situations, setSituations, options.situations)}</Grid>
            </Grid>
            <Grid container spacing={1.25}>
              <Grid item xs={12} md={6} lg={4}>
                <TextField fullWidth size="small" placeholder="N° marché, titulaire ou objet..." value={search} onChange={(event) => setSearch(event.target.value)} sx={partnerFilterFieldSx}
                  InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#667085' }} />, endAdornment: search ? <IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton> : null }} />
              </Grid>
            </Grid>
            <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 1.25 }}>
              <Box sx={{ alignItems: 'center', border: `1px solid ${partnerTableColors.border}`, borderRadius: '6px', display: 'flex', flex: 1, flexWrap: 'wrap', gap: 0.75, p: 1, width: '100%' }}>
                <Typography color="#52677c" fontSize={11} fontWeight={700} mr={0.5}>Grouper par :</Typography>
                {GROUP_OPTIONS.map(([value, label]) => <Button key={value} onClick={() => setGroupBy(value)} size="small" sx={partnerGroupButtonSx} variant={groupBy === value ? 'contained' : 'outlined'}>{label}</Button>)}
              </Box>
              <Box sx={{ alignItems: 'center', border: `1px solid ${partnerTableColors.border}`, borderRadius: '6px', display: 'flex', flexWrap: 'wrap', gap: 1, p: 1, width: { xs: '100%', lg: 'auto' } }}>
                <Typography color="#52677c" fontSize={11} fontWeight={700}>Période du contrat :</Typography>
                <TextField type="date" size="small" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ ...partnerFilterFieldSx, width: { xs: '100%', sm: 165 } }} />
                <Typography color="#52677c" fontSize={12}>→</Typography>
                <TextField type="date" size="small" value={dateTo} onChange={(event) => setDateTo(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ ...partnerFilterFieldSx, width: { xs: '100%', sm: 165 } }} />
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid #cbd8e6', borderRadius: '8px', overflow: 'hidden' }}>
        <Box sx={{ alignItems: 'center', backgroundColor: '#f8fbff', borderBottom: `1px solid ${partnerTableColors.border}`, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', px: 1.75, py: 1.5 }}>
          <Typography sx={{ color: '#1f2a44', fontSize: 13, fontWeight: 600 }}>Liste des marchés — 1 ligne = 1 marché</Typography>
          <Typography sx={{ color: '#4b6488', fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{filtered.length} marché(s)</Typography>
        </Box>
        <Box sx={{ height: 560 }}>
          <DataGrid
            rows={displayedRows}
            columns={columns}
            columnHeaderHeight={48}
            disableColumnSorting={groupBy !== 'none'}
            disableRowSelectionOnClick
            getRowClassName={(params) => params.row.isGroup ? 'partner-group-row' : ''}
            hideFooter={groupBy !== 'none'}
            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
            onPaginationModelChange={groupBy === 'none' ? (model) => { setPage(model.page); setRowsPerPage(model.pageSize); } : undefined}
            onRowDoubleClick={(params) => { if (!params.row.isGroup) openDetails(params.row); }}
            pageSizeOptions={[10, 15, 30, 50]}
            pagination={groupBy === 'none'}
            paginationModel={groupBy === 'none' ? { page, pageSize: rowsPerPage } : undefined}
            rowHeight={48}
            sx={partnerDataGridSx}
          />
        </Box>
        {groupBy !== 'none' && <Box sx={{ alignItems: 'center', borderTop: `1px solid ${partnerTableColors.border}`, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, justifyContent: 'space-between', px: 1.5, py: 1 }}>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <Typography color="#52677c" fontSize={11}>Cible marchés par page</Typography>
            <TextField select size="small" value={rowsPerPage} onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} sx={{ ...partnerFilterFieldSx, width: 82 }}>
              {[10, 15, 30, 50].map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <Typography color="#52677c" fontSize={11}>Page {Math.min(page + 1, Math.max(groupedPages.length, 1))} / {Math.max(groupedPages.length, 1)}</Typography>
            <Pagination count={Math.max(groupedPages.length, 1)} page={Math.min(page + 1, Math.max(groupedPages.length, 1))} onChange={(_, nextPage) => setPage(nextPage - 1)} shape="rounded" size="small" />
          </Box>
        </Box>}
      </Paper>

      <MarcheDetailsModal open={detailsOpen} market={selectedMarket} onClose={() => setDetailsOpen(false)} />
      <Snackbar open={Boolean(message)} autoHideDuration={3000} onClose={() => setMessage('')}><Alert severity="success">{message}</Alert></Snackbar>
    </PartnerMainContainer>
  );
};

export default Procurement;
